/**
 * WebSocket クライアント（§5 同期プロトコル）。
 * 自動再接続（指数バックオフ）とステータス通知だけを担当する薄いラッパ。
 */

import type { ClientMessage, ServerMessage } from '../shared/types';

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

export interface MessageMeta {
  parseMs: number;
  bytes: number;
}

export interface ConnectionOptions {
  room: string;
  name: string;
  onMessage: (message: ServerMessage, meta: MessageMeta) => void;
  onStatus: (status: ConnectionStatus) => void;
}

const INITIAL_RETRY_DELAY = 1000;
const MAX_RETRY_DELAY = 10_000;

export class RoomConnection {
  private socket: WebSocket | null = null;
  private retryDelay = INITIAL_RETRY_DELAY;
  private retryTimer: number | null = null;
  private disposed = false;

  constructor(private readonly options: ConnectionOptions) {
    this.connect();
  }

  private buildUrl(): string {
    const url = new URL('/api/room', window.location.href);
    url.protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    url.searchParams.set('room', this.options.room);
    url.searchParams.set('name', this.options.name);
    return url.toString();
  }

  private connect(): void {
    if (this.disposed) return;
    this.options.onStatus('connecting');

    const socket = new WebSocket(this.buildUrl());
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.retryDelay = INITIAL_RETRY_DELAY;
      this.options.onStatus('open');
    });

    socket.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      const startedAt = performance.now();
      try {
        const message = JSON.parse(event.data) as ServerMessage;
        this.options.onMessage(message, {
          parseMs: performance.now() - startedAt,
          bytes: event.data.length,
        });
      } catch {
        // 壊れたメッセージは黙って捨てる（サーバー側は常に JSON を送る）
      }
    });

    socket.addEventListener('close', () => {
      this.socket = null;
      this.options.onStatus('closed');
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      socket.close();
    });
  }

  private scheduleReconnect(): void {
    if (this.disposed || this.retryTimer !== null) return;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, this.retryDelay);
    this.retryDelay = Math.min(this.retryDelay * 2, MAX_RETRY_DELAY);
  }

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  send(message: ClientMessage): boolean {
    if (!this.isOpen) return false;
    this.socket!.send(JSON.stringify(message));
    return true;
  }

  dispose(): void {
    this.disposed = true;
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
    this.socket?.close();
    this.socket = null;
  }
}
